import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "metadataUrl",
    "importButton",
    "importStatus",
    "idpEntityId",
    "idpSsoServiceUrl",
    "idpSloServiceUrl",
    "idpCert",
    "idpCertFingerprint",
    "nameIdentifierFormat"
  ]

  static values = {
    url: String
  }

  declare metadataUrlTarget: HTMLInputElement
  declare importButtonTarget: HTMLButtonElement
  declare importStatusTarget: HTMLElement
  declare idpEntityIdTarget: HTMLInputElement
  declare idpSsoServiceUrlTarget: HTMLInputElement
  declare idpSloServiceUrlTarget: HTMLInputElement
  declare idpCertTarget: HTMLTextAreaElement
  declare idpCertFingerprintTarget: HTMLInputElement
  declare nameIdentifierFormatTarget: HTMLInputElement
  declare urlValue: string
  declare hasImportStatusTarget: boolean

  async import() {
    const url = this.metadataUrlTarget.value.trim()
    if (!url) {
      this.showStatus("Please enter a metadata URL", "error")
      return
    }

    this.setLoading(true)
    this.showStatus("Importing metadata...", "info")

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ metadata_url: url })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.populateFields(data.fields)
        this.showStatus("Metadata imported successfully!", "success")
      } else {
        this.showStatus(data.error || "Import failed", "error")
      }
    } catch (error) {
      this.showStatus("Network error. Please try again.", "error")
    } finally {
      this.setLoading(false)
    }
  }

  private populateFields(fields: Record<string, string | null>) {
    const mapping: Record<string, string> = {
      idp_entity_id: "idpEntityId",
      idp_sso_service_url: "idpSsoServiceUrl",
      idp_slo_service_url: "idpSloServiceUrl",
      idp_cert: "idpCert",
      idp_cert_fingerprint: "idpCertFingerprint",
      name_identifier_format: "nameIdentifierFormat"
    }

    for (const [field, targetName] of Object.entries(mapping)) {
      const value = fields[field]
      if (value) {
        const target = this[`${targetName}Target` as keyof this] as unknown as HTMLInputElement
        if (target) target.value = value
      }
    }
  }

  private setLoading(loading: boolean) {
    this.importButtonTarget.disabled = loading
    this.importButtonTarget.textContent = loading ? "Importing..." : "Import Metadata"
  }

  private showStatus(message: string, type: "success" | "error" | "info") {
    if (this.hasImportStatusTarget) {
      this.importStatusTarget.textContent = message
      this.importStatusTarget.className = `saml-provider-form-component__import-status saml-provider-form-component__import-status--${type}`
    }
  }
}
